import { Link, NavLink, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "@/auth";
import Mariposa from "@/components/Mariposa";
import http from "@/api";
import { ROLE_LABELS } from "@/constants";
import { LogOut, Users, Bell, Sparkles } from "lucide-react";
import AdminTour from "@/components/AdminTour";
import { TranslateWarning } from "@/components/TranslateWarning";

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [scope, setScope] = useState(null);
  const [unreadNotifs, setUnreadNotifs] = useState(0);
  const [supplierFlags, setSupplierFlags] = useState({
    supplier_self_assessment_enabled: false,
    supplier_document_upload_enabled: false,
    supplier_feedback_360_enabled: false,
    supplier_report_download_enabled: true,
  });

  useEffect(() => {
    if (!user) { setScope(null); setUnreadNotifs(0); return; }
    http.get("/me/scope").then((r) => setScope(r.data)).catch(() => setScope(null));
    http.get("/program/supplier-flags").then((r) => setSupplierFlags(r.data.flags || {})).catch(() => {});
    const fetchUnread = () => http.get("/notifications", { params: { unread_only: true, limit: 1 } })
      .then((r) => setUnreadNotifs(r.data?.unread || 0))
      .catch(() => setUnreadNotifs(0));
    fetchUnread();
    const interval = setInterval(fetchUnread, 60000); // poll every minute
    return () => clearInterval(interval);
  }, [user]);

  const links = [];
  if (user) {
    const phase1 = !supplierFlags.supplier_self_assessment_enabled
                   && !supplierFlags.supplier_document_upload_enabled
                   && !supplierFlags.supplier_feedback_360_enabled;
    if (user.role === "admin" || user.role === "evaluator" || user.role === "stakeholder") {
      links.push({ to: "/dashboard", label: "Vista general" });
      links.push({ to: "/proveedores", label: "Proveedores" });
      links.push({ to: "/admin/catalogo-kpis", label: "Catálogo de KPIs" });
    }
    if (user.role === "buyer" || user.role === "admin") {
      links.push({ to: "/comprador", label: "Comprador" });
    }
    if (user.role === "buyer") {
      links.push({ to: "/comprador/pesos", label: "Pesos de mi categoría" });
    }
    if (user.role === "admin") {
      links.push({ to: "/comprador/mis-proveedores", label: "Carga de proveedores" });
    }
    if (user.role === "admin" || user.role === "evaluator" || user.role === "buyer") {
      links.push({ to: "/captura-mensual", label: "Captura mensual" });
    }
    if (["admin", "owner", "buyer", "evaluator", "stakeholder"].includes(user.role)) {
      links.push({ to: "/impacto-economico", label: "Impacto económico" });
    }
    if (user.role === "admin" || user.role === "evaluator") {
      // /evaluar (escala 0-10) es legacy/demo: oculto del menú. Fuente oficial de captura: /captura-mensual
      // Phase 2 admin tools — only visible when corresponding supplier flag is enabled
      if (!phase1) {
        if (supplierFlags.supplier_self_assessment_enabled) {
          links.push({ to: "/admin/cuestionarios", label: "Cuestionarios" });
        }
        if (supplierFlags.supplier_document_upload_enabled) {
          links.push({ to: "/admin/seguimiento", label: "Seguimiento" });
        }
      }
    }
    if (user.role === "admin") {
      links.push({ to: "/admin/aprobaciones", label: "Aprobaciones y solicitudes" });
      links.push({ to: "/admin/configuracion", label: "Configuración" });
      links.push({ to: "/portal-ti", label: "Portal TI" });
      links.push({ to: "/admin/aprendizaje", label: "Admin Aprendizaje" });
      links.push({ to: "/presentacion", label: "Modo presentación" });
    }
    if (user.role === "stakeholder") {
      links.push({ to: "/presentacion", label: "Modo presentación" });
    }
    if (user.role === "owner") {
      // DEV-only profile: gets internal panel + Admin Aprendizaje.
      links.push({ to: "/dev", label: "DEV" });
      links.push({ to: "/portal-ti", label: "Portal TI" });
      links.push({ to: "/admin/aprendizaje", label: "Admin Aprendizaje" });
    }
    if (user.role === "tech_admin") {
      links.push({ to: "/portal-ti", label: "Portal TI" });
      links.push({ to: "/readiness", label: "Readiness técnico" });
      links.push({ to: "/architecture-review", label: "Architecture Review" });
    }
    if (user.role === "supplier") {
      links.push({ to: "/mi-evaluacion", label: "Mi desempeño" });
      // Phase 2 modules — only visible when feature flag is on
      if (supplierFlags.supplier_self_assessment_enabled) {
        links.push({ to: "/mi-cuestionario", label: "Mi cuestionario" });
      }
      if (supplierFlags.supplier_document_upload_enabled) {
        links.push({ to: "/mis-documentos", label: "Mis documentos" });
      }
      if (supplierFlags.supplier_feedback_360_enabled) {
        links.push({ to: "/voz-proveedor", label: "Mi voz a Lala" });
      }
    }
    links.push({ to: "/aprendizaje", label: "Aprendizaje" });
    links.push({ to: "/guia-uso", label: "Guía de uso" });
  }

  return (
    <div className="min-h-screen flex flex-col paper relative">
      <header className="sticky top-0 z-30" style={{ backgroundColor: "var(--lala-blue)" }}>
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between gap-4">
          <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 group shrink-0">
            <img
              src="/brand/lala_elite_badge_sm.png"
              alt="Lala Elite"
              className="h-16 w-16 object-contain group-hover:scale-105 transition-transform"
              draggable={false}
            />
            <div className="leading-tight hidden sm:block">
              <div className="font-display text-lg text-white leading-none">Lala Elite</div>
              <div className="font-hand text-[11px] text-white/80 whitespace-nowrap mt-0.5">construidos para crecer juntos</div>
            </div>
          </Link>

          <nav className="hidden md:flex items-center gap-1 flex-wrap justify-center min-w-0">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-${l.to.replace(/\//g, "")}`}
                className={({ isActive }) =>
                  `px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                    isActive
                      ? "bg-white text-[var(--lala-blue)]"
                      : "text-white/90 hover:bg-white/15"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            {user ? (
              <>
                {scope && scope.scoped && scope.role !== "supplier" && (
                  <div className="hidden sm:flex items-center gap-1.5 text-xs bg-amber-50 border border-amber-200 text-amber-900 rounded-full px-2.5 py-1"
                    data-testid="scope-badge"
                    title={`Asignación: ${(scope.assigned_categories || []).join(", ") || "—"} · ${scope.assigned_supplier_ids?.length || 0} proveedores específicos`}>
                    <Users size={12} />
                    <span className="font-semibold">{scope.visible_count}</span> / {scope.total_count} proveedores asignados
                  </div>
                )}
                {["admin", "evaluator", "buyer", "stakeholder", "supplier"].includes(user.role) && (
                  <button
                    onClick={() => window.dispatchEvent(new Event("lala:start-tour"))}
                    data-testid="start-tour-btn"
                    title="Tour guiado de la plataforma"
                    className="w-9 h-9 rounded-full flex items-center justify-center bg-white/10 hover:bg-white/25 transition"
                  >
                    <Sparkles size={16} className="text-white" />
                  </button>
                )}
                <Link
                  to="/notificaciones"
                  data-testid="notif-bell"
                  title="Notificaciones"
                  className="relative w-9 h-9 rounded-full flex items-center justify-center bg-white/10 hover:bg-white/25 transition"
                >
                  <Bell size={16} className="text-white" />
                  {unreadNotifs > 0 && (
                    <span
                      data-testid="notif-bell-badge"
                      className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[var(--lala-red)] text-white text-[10px] font-bold flex items-center justify-center"
                    >
                      {unreadNotifs > 99 ? "99+" : unreadNotifs}
                    </span>
                  )}
                </Link>
                <div className="hidden sm:block text-right leading-tight">
                  <div className="text-sm font-semibold text-white">{user.name}</div>
                  <div className="text-[11px] uppercase tracking-wider text-white/70">{ROLE_LABELS[user.role] || user.role}</div>
                </div>
                <button
                  data-testid="logout-btn"
                  onClick={async () => { await logout(); nav("/"); }}
                  className="sketch-pill bg-white text-[var(--lala-blue)] px-3 py-1.5 text-sm font-semibold flex items-center gap-1.5"
                >
                  <LogOut size={14} /> Salir
                </button>
              </>
            ) : (
              <Link to="/login" data-testid="nav-login" className="sketch-pill bg-white text-[var(--lala-blue)] px-4 py-2 text-sm font-bold">
                Iniciar sesión
              </Link>
            )}
          </div>
        </div>
      </header>

      <main className="flex-1 relative z-10">{children}</main>
      <AdminTour />
      <TranslateWarning />
    </div>
  );
}
